import type { CSSProperties, ReactNode, Ref } from "react"
import { forwardRef } from "react"
import { Link as RouterLink } from "react-router-dom"
import type { WithProductPickFragment } from "~/graphql"
import { useAnalytics } from "~/providers/AnalyticsProvider"
import { useConfigContext } from "~/providers/ConfigProvider"

interface Props {
  to: string | null | undefined
  className?: string
  style?: CSSProperties
  title?: string
  "aria-label"?: string
  openInNewWindow?: boolean
  openAsExternal?: boolean
  trackClick?: () => {
    event: string
    data: Record<
      string,
      string | number | null | WithProductPickFragment | undefined
    >
  }
  children?: ReactNode
  handleClick?: () => void
}

const Link = (
  {
    trackClick,
    to,
    children,
    openInNewWindow,
    openAsExternal,
    handleClick,
    ...props
  }: Props,
  ref: Ref<HTMLAnchorElement>
) => {
  const { track } = useAnalytics()
  const config = useConfigContext()

  if (!to) return <>{children}</>

  if (openAsExternal || isExternalUrl(to) || isAnchor(to)) {
    return (
      <a
        href={to}
        onClick={e => {
          if (isAnchor(to)) {
            e.preventDefault()
            const targetId = to.substring(1)
            const targetElement = document.getElementById(targetId)
            if (targetElement) {
              targetElement.scrollIntoView({ behavior: "smooth" })
            }
          }
          if (trackClick && !config.disableGTM) {
            track(trackClick().event, trackClick().data)
          }
          if (handleClick) {
            handleClick()
          }
        }}
        {...(openInNewWindow ? { target: "_blank", rel: "noreferrer" } : {})}
        {...props}
      >
        {children}
      </a>
    )
  }

  return (
    <RouterLink
      to={to}
      onClick={() => {
        // We can't use refCallback and trackLink here because it would load href with refresh.
        if (trackClick) {
          track(trackClick().event, trackClick().data)
        }
        if (handleClick) {
          handleClick()
        }
      }}
      {...(openInNewWindow ? { target: "_blank", rel: "noreferrer" } : {})}
      {...props}
      ref={ref}
    >
      {children}
    </RouterLink>
  )
}

const isExternalUrl = (href: string) =>
  href.match(/(https?:)|(mailto:)|(data:)/)

const isAnchor = (href: string) => href.startsWith("#")

export default forwardRef(Link)
